import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function OnSiteServicePage() {
  useSEO({
    title: '현장 출장 서비스 - KR STUDIO | 전문 촬영팀 파견',
    description: 'KR스튜디오의 현장 출장 서비스로 어디서든 전문적인 콘텐츠 제작이 가능합니다. 전문 장비와 숙련된 스태프가 현장으로 찾아갑니다.',
    keywords: '현장 출장, 촬영팀 파견, 현장 촬영, 출장 제작, 이동형 스튜디오',
    canonical: '/on-site-service',
    schema: createServiceSchema(
      '현장 출장 서비스',
      '전문 장비와 숙련된 스태프가 현장으로 찾아가는 콘텐츠 제작 서비스'
    ),
  });

  const { t } = useTranslation();
  const [selectedLocation, setSelectedLocation] = useState('classroom');

  const equipment = [
    {
      category: '📹 카메라 시스템',
      image: 'https://public.readdy.ai/ai/img_res/a294ae8e361d0c0ed0a51817d51c8920.jpg',
      items: [
        'Sony FX6 (4K 120fps)',
        '무선 송출 시스템',
        '3축 짐벌 스태빌라이저'
      ]
    },
    {
      category: '🎤 음향 장비',
      image: 'https://readdy.ai/api/search-image?query=Professional%20wireless%20lavalier%20microphone%20system%20with%20boom%20mic%20and%20shotgun%20microphone%2C%20digital%20audio%20mixer%20on%20white%20background%2C%20studio%20product%20photography%2C%20clean%20minimalist%20setup%2C%20broadcast%20audio%20equipment&width=600&height=400&seq=eq2&orientation=landscape',
      items: [
        '무선 핀마이크 8채널',
        '붐마이크 & 샷건마이크',
        '디지털 믹서'
      ]
    },
    {
      category: '💡 조명 장비',
      image: 'https://readdy.ai/api/search-image?query=Professional%20LED%20panel%20lighting%20kit%20with%20portable%20diffusers%20and%20reflectors%20on%20white%20background%2C%20studio%20product%20photography%2C%20clean%20minimalist%20setup%2C%20professional%20video%20lighting%20equipment&width=600&height=400&seq=eq3&orientation=landscape',
      items: [
        'LED 패널 조명 4세트',
        '휴대용 디퓨저',
        '반사판 & 블랙아웃'
      ]
    }
  ];

  const locationSolutions = [
    {
      id: 'classroom',
      icon: 'ri-presentation-line',
      title: '강의실/세미나실',
      solutions: [
        '음향 반사 최소화 세팅',
        '프로젝터 화면 동시 녹화',
        '질의응답 마이크 운영'
      ]
    },
    {
      id: 'outdoor',
      icon: 'ri-sun-line',
      title: '야외 행사',
      solutions: [
        '바람소리 차단 장비',
        '태양광 대응 필터',
        '무선 중계 시스템'
      ]
    },
    {
      id: 'office',
      icon: 'ri-building-4-line',
      title: '기업 회의실',
      solutions: [
        '화이트보드 선명 촬영',
        '다자간 회의 멀티캠',
        '기밀 보안 체크'
      ]
    }
  ];

  const processSteps = [
    {
      day: 'D-3',
      icon: '📞',
      title: '사전 협의',
      color: 'from-blue-500 to-blue-600',
      tasks: [
        '현장 도면 확인',
        '필요 장비 선정',
        '동선 계획 수립'
      ]
    },
    {
      day: 'D-1',
      icon: '🚚',
      title: '장비 점검',
      color: 'from-purple-500 to-purple-600',
      tasks: [
        '모든 장비 테스트',
        '백업 장비 준비',
        '이동 경로 확인'
      ]
    },
    {
      day: 'D-Day',
      icon: '🎬',
      title: '촬영',
      color: 'from-red-500 to-red-600',
      tasks: [
        '2시간 전 도착',
        '30분 내 세팅',
        '정숙 촬영 진행',
        '15분 내 철수'
      ]
    },
    {
      day: 'D+1',
      icon: '✂️',
      title: '편집',
      color: 'from-green-500 to-green-600',
      tasks: [
        '당일 1차 편집본',
        '피드백 반영',
        '최종 납품'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20video%20production%20crew%20setting%20up%20camera%20equipment%20on%20location%20in%20modern%20corporate%20office%2C%20filming%20team%20working%20with%20professional%20cameras%20and%20lighting%2C%20clean%20organized%20workspace%2C%20natural%20daylight%2C%20professional%20atmosphere&width=1920&height=800&seq=oshero2&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 px-6 py-3 rounded-full mb-6">
              <span className="text-blue-300 font-bold">🗺️ 전국 어디든 48시간 내 도착</span>
            </div>
            <h1 className="text-6xl font-black mb-6 leading-tight">
              우리가 찾아가는<br />
              스튜디오
            </h1>
            <p className="text-2xl mb-8 text-gray-300 font-light">
              현장은 그대로, 품질은 스튜디오처럼
            </p>
            <div className="flex gap-4 hidden">
              <a href="mailto:contact@krstudio.com" className="bg-blue-500 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-600 transition cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl">
                출장 문의하기
              </a>
              <a href="#equipment" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-gray-900 transition cursor-pointer whitespace-nowrap">
                장비 보기
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Approach */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block bg-gray-900 text-white px-6 py-2 rounded-full mb-6">
                <span className="font-bold">🤫 조용한 프로페셔널</span>
              </div>
              <h2 className="text-4xl font-black text-gray-900 mb-6">
                "촬영팀이 있다는 걸<br />잊게 만듭니다"
              </h2>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-black text-blue-500 mb-2">2시간 전</div>
                  <p className="text-gray-600">현장 도착</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-blue-500 mb-2">30분 내</div>
                  <p className="text-gray-600">세팅 완료</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-blue-500 mb-2">15분 내</div>
                  <p className="text-gray-600">철수 완료</p>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-8">
                <p className="text-lg text-gray-700 leading-relaxed text-center">
                  촬영 중에는 <strong className="text-gray-900">무음 수신호로만 소통</strong>하며, 
                  현장의 흐름을 전혀 방해하지 않는 것이 우리의 철칙입니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section id="equipment" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4">장비 스펙</h2>
            <p className="text-xl text-gray-600">스튜디오급 전문 장비를 현장으로</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {equipment.map((eq, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className="aspect-[3/2] overflow-hidden bg-white">
                  <img 
                    src={eq.image} 
                    alt={eq.category}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-black text-gray-900 mb-6">{eq.category}</h3>
                  <ul className="space-y-3">
                    {eq.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-blue-500 mr-3 mt-1 flex-shrink-0"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Solutions */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4">현장별 솔루션</h2>
            <p className="text-xl text-gray-600">각 환경에 최적화된 촬영 세팅</p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="flex justify-center gap-4 mb-12">
              {locationSolutions.map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => setSelectedLocation(loc.id)}
                  className={`flex items-center gap-3 px-8 py-4 rounded-xl font-bold transition-all whitespace-nowrap cursor-pointer ${
                    selectedLocation === loc.id
                      ? 'bg-blue-500 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                  }`}
                >
                  <i className={`${loc.icon} text-2xl`}></i>
                  <span>{loc.title}</span>
                </button>
              ))}
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
              {locationSolutions.map((loc) => (
                selectedLocation === loc.id && (
                  <div key={loc.id} className="animate-fadeIn">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center">
                        <i className={`${loc.icon} text-3xl text-white`}></i>
                      </div>
                      <h3 className="text-3xl font-black text-gray-900">{loc.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      {loc.solutions.map((solution, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-xl p-6 hover:bg-blue-50 transition-all">
                          <div className="flex items-start gap-3">
                            <i className="ri-check-double-line text-2xl text-blue-500 flex-shrink-0"></i>
                            <span className="text-gray-700 font-medium">{solution}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4">출장 프로세스</h2>
            <p className="text-xl text-gray-600">체계적인 준비와 완벽한 실행</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {processSteps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-2">
                  <div className="text-6xl mb-6 text-center">{step.icon}</div>
                  <div className="text-center mb-6">
                    <div className="inline-block bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-bold mb-3">{step.day}</div>
                    <h3 className="text-2xl font-black text-gray-900">{step.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {step.tasks.map((task, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-blue-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-4xl text-blue-500"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Map */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4">출장 가능 지역</h2>
            <div className="inline-flex items-center gap-3 bg-blue-500 text-white px-8 py-4 rounded-full text-xl font-bold">
              <i className="ri-map-pin-line text-2xl"></i>
              <span>전국 어디든 48시간 내 도착</span>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group cursor-pointer">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-building-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">수도권</h3>
                <p className="text-gray-600">서울 · 경기 · 인천</p>
              </div>
              
              <div className="text-center group cursor-pointer">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-map-2-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">전국</h3>
                <p className="text-gray-600">충청 · 호남 · 영남</p>
              </div>
              
              <div className="text-center group cursor-pointer">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-plane-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">제주 · 해외</h3>
                <p className="text-gray-600">별도 문의</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-5xl font-black mb-6">
            현장 촬영이 필요하신가요?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            전문 장비와 경험 많은 스태프가<br />
            여러분의 현장으로 찾아갑니다
          </p>
          <div className="flex gap-4 justify-center hidden">
            <a href="tel:070-8656-8134" className="bg-blue-500 text-white px-12 py-5 rounded-xl text-lg font-bold hover:bg-blue-600 transition cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl">
              견적 문의
            </a>
            <a href="tel:070-8656-8134" className="border-2 border-white text-white px-12 py-5 rounded-xl text-lg font-bold hover:bg-white hover:text-gray-900 transition cursor-pointer whitespace-nowrap">
              긴급 문의
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}
